import { useEffect, useState } from "react"
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { useToast } from "@/hooks"
import { useTranslation } from "react-i18next"
import { Wand2, Check } from "lucide-react"
import {
  applyValueToField,
  isApplySuggestionTargetCurrent,
  isFieldApplyAvailable,
  isFieldEmpty,
  notifySuggestionApplyEvent,
  subscribeFieldApplyEntries,
} from "@/components/Copilot/applySuggestion/applySuggestionHandler"
import ConfirmReplaceDialog from "@/components/Copilot/applySuggestion/ConfirmReplaceDialog"

interface ApplySuggestionButtonProps {
  applyKey: string
  content: string
  className?: string
  applyTargetPath?: string
}

export const ApplySuggestionButton = ({
  applyKey,
  content,
  className,
  applyTargetPath,
}: ApplySuggestionButtonProps) => {
  const { t } = useTranslation()
  const { alertToast } = useToast()
  const [isApplied, setIsApplied] = useState(false)
  const [isAvailable, setIsAvailable] = useState<boolean>(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const applySuggestion = async () => {
    notifySuggestionApplyEvent({
      phase: "start",
      source: "single",
      requestedKeys: [applyKey],
      applyTargetPath,
    })
    const ok = await applyValueToField(applyKey, content)
    notifySuggestionApplyEvent({
      phase: "complete",
      source: "single",
      requestedKeys: [applyKey],
      appliedKeys: ok ? [applyKey] : [],
      applyTargetPath,
    })
    return ok
  }

  // disable the apply suggestion button if the field key is not found in the page
  useEffect(() => {
    const check = () =>
      setIsAvailable(
        isFieldApplyAvailable(applyKey) &&
          isApplySuggestionTargetCurrent(applyTargetPath)
      )
    check()
    return subscribeFieldApplyEntries(check)
  }, [applyTargetPath, applyKey])

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="icon"
            variant="outline"
            className={`border-0 ${className || ""} ${!isAvailable ? "opacity-50 cursor-not-allowed" : ""}`}
            style={{ backgroundColor: "transparent" }}
            onMouseDown={(e) => {
              // Prevent focus changes from causing scroll jumps
              e.preventDefault()
            }}
            onClick={async (e) => {
              e.preventDefault()
              e.stopPropagation()
              if (
                !isAvailable ||
                !isApplySuggestionTargetCurrent(applyTargetPath)
              ) {
                setIsAvailable(false)
                return
              }
              // Confirm overwrite if target field is not empty
              if (!isFieldEmpty(applyKey)) {
                setConfirmOpen(true)
                return
              }
              const ok = await applySuggestion()
              if (ok) {
                setIsApplied(true)
                setTimeout(() => setIsApplied(false), 2000)
              } else {
                alertToast?.({
                  title: t("common.copilot.applyFailed"),
                  description: t("common.copilot.applyFailedDesc"),
                })
              }
            }}
          >
            {isApplied ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Wand2 className="h-4 w-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left">
          {isAvailable
            ? t("common.copilot.applyHint")
            : t("common.copilot.applyHintDisabled")}
        </TooltipContent>
      </Tooltip>
      <ConfirmReplaceDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={async () => {
          if (!isApplySuggestionTargetCurrent(applyTargetPath)) {
            setConfirmOpen(false)
            setIsAvailable(false)
            return
          }
          const ok = await applySuggestion()
          setConfirmOpen(false)
          if (ok) {
            setIsApplied(true)
            setTimeout(() => setIsApplied(false), 2000)
          } else {
            alertToast?.({
              title: t("common.copilot.applyFailed"),
              description: t("common.copilot.applyFailedDesc"),
            })
          }
        }}
      />
    </TooltipProvider>
  )
}

export default ApplySuggestionButton
